import { Component } from '@theme/component';
import { QuickAddComponent } from '@theme/quick-add';
import { isClickedOutside, isMobileBreakpoint, isTouchDevice, mediaQueryLarge } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLButtonElement} trigger - The button element.
 * @property {HTMLAnchorElement} productLink - The product link element.
 *
 * @extends Component<Refs>
 */

export class ProductHotspotComponent extends Component {
  requiredRefs = ['trigger', 'dialog'];
  /** @type {(() => void) | null} */
  #pointerenterHandler = null;
  timer = /** @type {number | null} */ (null);
  /** @type {number | null} */
  #closeTimer = null;

  connectedCallback() {
    super.connectedCallback();
    this.#handleBreakpointChange();
    mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange);
    // pointerover bubbles, so this fires for the trigger, the dialog and
    // everything inside it - any of them means the pointer is still with us.
    this.addEventListener('pointerover', this.#cancelPendingClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#removeDesktopListeners();
    this.#removeMobileListeners();
    this.#cancelPendingClose();
    this.removeEventListener('pointerover', this.#cancelPendingClose);
    mediaQueryLarge.removeEventListener('change', this.#handleBreakpointChange);
  }

  /**
   * Cancel a pending close.
   * @returns {void}
   */
  #cancelPendingClose = () => {
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
  };

  /**
   * Close after a short grace period rather than immediately.
   *
   * The bridge between the trigger and the dialog is a fixed clip-path, so a
   * natural diagonal from the dot towards the card drops out of it and lands on
   * the background for a frame or two. That is most of the way to the quick-add
   * button when the card opens to the left, because then the button sits in the
   * corner nearest the dot. Closing on that instant makes the card unreachable
   * unless the customer traces an exact path, so give them time to arrive.
   * @returns {void}
   */
  #scheduleClose = () => {
    this.#cancelPendingClose();
    this.#closeTimer = setTimeout(() => {
      this.#closeTimer = null;
      this.closeDialog();
    }, 260);
  };

  /**
   * Open the quick-add modal
   * @returns {void}
   */
  #openQuickAddModal() {
    const quickAddComponent = /** @type {QuickAddComponent | null} */ (this.querySelector('quick-add-component'));
    if (!quickAddComponent) return;
    quickAddComponent.handleClick(new MouseEvent('click', { bubbles: true, cancelable: true }));
  }

  /**
   * Set up mobile touch event listeners
   * @returns {void}
   */
  #setupMobileListeners() {
    const { trigger } = this.refs;
    trigger.addEventListener('touchstart', this.openDialogTouch);
  }

  /**
   * Remove mobile touch event listeners
   * @returns {void}
   */
  #removeMobileListeners() {
    const { trigger } = this.refs;
    trigger.removeEventListener('touchstart', this.openDialogTouch);
  }

  /**
   * Set up desktop event listeners (hover)
   * @returns {void}
   */
  #setupDesktopListeners() {
    const { trigger, dialog } = this.refs;

    /** @type {() => void} */
    const pointerenterHandler = () => {
      if (dialog.open) return;
      this.timer = setTimeout(() => {
        this.showDialog();
      }, 120);
      trigger.addEventListener('pointerleave', this.#handlePointerLeave);
    };

    this.#pointerenterHandler = pointerenterHandler;
    trigger.addEventListener('pointerenter', pointerenterHandler);
  }

  /**
   * Remove desktop event listeners from trigger
   * @returns {void}
   */
  #removeDesktopListeners() {
    const { trigger } = this.refs;

    if (this.#pointerenterHandler) {
      trigger.removeEventListener('pointerenter', this.#pointerenterHandler);
      trigger.removeEventListener('pointerleave', this.#handlePointerLeave);
      this.#pointerenterHandler = null;
    }

    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  /**
   * Switch between desktop hover and mobile touch listeners.
   */
  #handleBreakpointChange = () => {
    this.#removeDesktopListeners();
    this.#removeMobileListeners();

    if (isTouchDevice() || isMobileBreakpoint()) {
      this.#setupMobileListeners();
    } else {
      this.#setupDesktopListeners();
    }
  };

  /**
   * Calculate the placement of the dialog.
   * @returns {Promise<void> | undefined}
   */
  #calculateDialogPlacement() {
    const { trigger, dialog } = this.refs;
    const hotspotsContainer = this.parentElement;

    if (!hotspotsContainer) return;

    const BUTTON_GAP = 10;
    const CONTAINER_GAP = 10;
    const TOTAL_GAP = BUTTON_GAP + CONTAINER_GAP;

    const containerRect = hotspotsContainer?.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();

    dialog.style.visibility = 'hidden';
    dialog.style.display = 'block';
    dialog.style.transform = 'none';
    dialog.removeAttribute('data-placement');

    const { width: dialogWidth, height: dialogHeight } = dialog.getBoundingClientRect();

    dialog.style.removeProperty('display');
    dialog.style.removeProperty('visibility');
    dialog.style.removeProperty('transform');

    const buttonLeft = triggerRect.left - containerRect.left;
    const buttonRight = triggerRect.right - containerRect.left;
    const buttonTop = triggerRect.top - containerRect.top;
    const buttonBottom = triggerRect.bottom - containerRect.top;

    const spaceRight = containerRect.width - buttonRight - CONTAINER_GAP;
    const spaceLeft = buttonLeft - CONTAINER_GAP;

    let x = 'right';
    if (spaceRight >= dialogWidth + BUTTON_GAP) { x = 'right'; }
    else if (spaceLeft >= dialogWidth + BUTTON_GAP) { x = 'left'; }
    else { x = 'center'; }

    let y = 'bottom';
    let verticalOffset = 0;
    let horizontalOffset = 0;

    if (x !== 'center') {
      let dialogStartY = buttonTop;
      let dialogEndY = buttonTop + dialogHeight;

      if (dialogEndY > containerRect.height - CONTAINER_GAP) {
        dialogStartY = buttonBottom - dialogHeight;
        dialogEndY = buttonBottom;
        y = 'top';

        if (dialogStartY < CONTAINER_GAP) {
          verticalOffset = CONTAINER_GAP - dialogStartY;
        } else if (dialogEndY > containerRect.height - CONTAINER_GAP) {
          verticalOffset = -(dialogEndY - (containerRect.height - CONTAINER_GAP));
        }
      } else {
        if (dialogStartY < CONTAINER_GAP) {
          verticalOffset = CONTAINER_GAP - dialogStartY;
          y = 'bottom';
        }
      }
    } else {
      if (containerRect.height - buttonBottom >= dialogHeight + TOTAL_GAP) { y = 'bottom'; }
      else if (buttonTop >= dialogHeight + TOTAL_GAP) { y = 'top'; }
      else { y = buttonTop < containerRect.height / 2 ? 'bottom' : 'top'; }

      // A centered dialog is centered on the button, which pushes it out of the
      // container whenever the button sits near an edge. Nudge it back in, the
      // same way verticalOffset does for the other axis.
      const buttonCenterX = (buttonLeft + buttonRight) / 2;
      const dialogStartX = buttonCenterX - dialogWidth / 2;
      const dialogEndX = buttonCenterX + dialogWidth / 2;

      if (dialogStartX < CONTAINER_GAP) {
        horizontalOffset = CONTAINER_GAP - dialogStartX;
      } else if (dialogEndX > containerRect.width - CONTAINER_GAP) {
        horizontalOffset = -(dialogEndX - (containerRect.width - CONTAINER_GAP));
      }
    }

    dialog.dataset.placement = `${x},${y}`;

    if (verticalOffset !== 0) {
      dialog.style.setProperty('--dialog-vertical-offset', `${verticalOffset}px`);
    } else {
      dialog.style.removeProperty('--dialog-vertical-offset');
    }

    if (horizontalOffset !== 0) {
      dialog.style.setProperty('--dialog-horizontal-offset', `${horizontalOffset}px`);
    } else {
      dialog.style.removeProperty('--dialog-horizontal-offset');
    }

    return new Promise((resolve) => setTimeout(resolve, 100));
  }

  /**
   * Handle pointer leave.
   * @param {PointerEvent} e
   * @returns {void}
   */
  #handlePointerLeave = (e) => {
    const { dialog, trigger } = this.refs;

    if (this.timer) { clearTimeout(this.timer); this.timer = null; }
    if (!dialog.open) return;

    // The safety triangle is an ::after on the dialog, and its clip-path leaves
    // gaps the pointer can cross on its way from the trigger to the dialog. When
    // that happens the pointer lands on this component itself, which the checks
    // below read as "left the hotspot" and the dialog closes mid-travel.
    // Anything still inside this component is not a leave.
    if (e.relatedTarget instanceof Node && this.contains(e.relatedTarget)) return;

    const isLeavingTrigger = e.target === trigger;
    const isLeavingDialog = e.target === dialog;
    const isGoingToDialog =
      e.relatedTarget === dialog ||
      (e.relatedTarget instanceof Element && e.relatedTarget.closest('dialog') === dialog);
    const isGoingToTrigger = e.relatedTarget === trigger;

    if ((isLeavingTrigger && !isGoingToDialog) || (isLeavingDialog && !isGoingToTrigger)) {
      this.#scheduleClose();
    }
  };

  /**
   * Get the product link for the hotspot product.
   * @returns {HTMLAnchorElement | null}
   */
  getHotspotProductLink() {
    return this.refs.productLink || null;
  }

  /**
   * Handle hotspot click.
   * @param {MouseEvent} e
   * @returns {void}
   */
  handleHotspotClick = (e) => {
    if (isMobileBreakpoint() || isTouchDevice()) {
      e.preventDefault();
      e.stopPropagation();
      this.#openQuickAddModal();
    } else {
      this.showDialog();
    }
  };

  showDialog = async () => {
    const { dialog } = this.refs;

    this.#cancelPendingClose();

    // Prevent double-open if already showing
    if (dialog.dataset.showing === 'true' || dialog.open) return;

    if (isTouchDevice() || isMobileBreakpoint()) {
      // Mobile: place the dialog, but deliberately don't await the settle delay
      // so the first tap is still always visible. Skipping placement entirely
      // leaves the card at its static position, where it can run past the edge
      // of the screen - up to 186px of a 292px card on a 375px viewport.
      this.#calculateDialogPlacement();
      dialog.dataset.showing = 'true';
      dialog.show();
      setTimeout(() => {
        document.body.addEventListener('touchstart', this.lightDismissTouch);
      }, 500);
    } else {
      // Desktop: calculate placement then open
      await this.#calculateDialogPlacement();
      dialog.dataset.showing = 'true';
      dialog.show();
      document.body.addEventListener('click', this.lightDismissMouse);
      document.body.addEventListener('keydown', this.lightDismissKeyboard);
      document.body.addEventListener('keyup', this.lightDismissKeyboard);
      this.refs.dialog.addEventListener('pointerleave', this.#handlePointerLeave);
    }
  };

  /**
   * Close the dialog.
   * @returns {Promise<void>}
   */
  closeDialog = async () => {
    const { dialog, trigger } = this.refs;
    this.#cancelPendingClose();
    dialog.dataset.closing = 'true';
    dialog.close();
    document.body.removeEventListener('touchstart', this.lightDismissTouch);
    document.body.removeEventListener('click', this.lightDismissMouse);
    document.body.removeEventListener('keydown', this.lightDismissKeyboard);
    document.body.removeEventListener('keyup', this.lightDismissKeyboard);
    dialog.removeEventListener('pointerleave', this.#handlePointerLeave);
    trigger.removeEventListener('pointerleave', this.#handlePointerLeave);
    const animations = dialog.getAnimations({ subtree: true });
    await Promise.allSettled(animations.map((a) => a.finished));
    if (!dialog.open) {
      delete dialog.dataset.showing;
      delete dialog.dataset.closing;
      delete dialog.dataset.placement;
    }
  };

  /**
   * Light dismiss on touch outside (mobile).
   * @param {TouchEvent} event
   * @returns {void}
   */
  lightDismissTouch = (event) => {
    const { dialog } = this.refs;
    if (isClickedOutside(event.touches[0], dialog)) {
      this.closeDialog();
    }
  };

  /**
   * Open dialog on touch.
   * @param {TouchEvent} event
   * @returns {void}
   */
  openDialogTouch = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.showDialog();
  };

  /**
   * Light dismiss on mouse click (desktop).
   * @param {MouseEvent} event
   * @returns {void}
   */
  lightDismissMouse = (event) => {
    const { dialog } = this.refs;
    // Clicks on the trigger or the safety triangle report the dialog as the
    // event target, so isClickedOutside falls back to a bounds check that
    // excludes both of them and dismisses the dialog the user is aiming for.
    // Treat anything inside this component as inside.
    if (event.target instanceof Node && this.contains(event.target)) return;
    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };

  /**
   * Light dismiss on keyboard (desktop).
   * @param {KeyboardEvent} event
   * @returns {void}
   */
  lightDismissKeyboard = (event) => {
    const { dialog } = this.refs;
    if (
      (event.type === 'keydown' && event.key === 'Escape') ||
      (event.type === 'keyup' && !dialog.matches(':is(:focus, :focus-visible, :focus-within)'))
    ) {
      this.closeDialog();
    }
  };
}

customElements.define('product-hotspot-component', ProductHotspotComponent);